import { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Mail, Package, DollarSign, Search, Eye, ChevronLeft, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerManagement = () => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

  useEffect(() => {
    fetchCustomers();
  }, [searchQuery]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers`, {
        params: { search: searchQuery, limit: pagination.limit, offset: pagination.offset },
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data.customers);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      toast.error('Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerDetails(response.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailsModal(true);
    fetchCustomerDetails(customer.id);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCustomers();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Management
            </span>
            <span className="text-sm font-normal text-gray-500">
              {pagination.total} total customers
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="customer-search"
                />
              </div>
              <Button type="submit" variant="outline">
                Search
              </Button>
            </div>
          </form>

          {/* Customer List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No customers found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  data-testid={`customer-${customer.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold">{customer.full_name}</span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {customer.email}
                        </p>
                        <p className="text-xs text-gray-400">
                          Joined: {new Date(customer.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <Package className="h-4 w-4" />
                          <span className="font-bold">{customer.total_orders}</span>
                        </div>
                        <p className="text-xs text-gray-400">Orders</p>
                      </div>

                      <div className="text-center">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <DollarSign className="h-4 w-4" />
                          <span className="font-bold">${customer.total_spent?.toFixed(2) || '0.00'}</span>
                        </div>
                        <p className="text-xs text-gray-400">Spent</p>
                      </div>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewCustomer(customer)}
                        data-testid={`view-customer-${customer.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Customer Details
            </DialogTitle>
          </DialogHeader>

          {detailsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
          ) : customerDetails ? (
            <div className="space-y-6 py-4">
              {/* Customer Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold mb-3">{customerDetails.customer.full_name}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{customerDetails.customer.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Joined</p>
                    <p className="font-medium">{new Date(customerDetails.customer.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Orders</p>
                    <p className="font-bold text-lg">{customerDetails.stats.total_orders}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Spent</p>
                    <p className="font-bold text-lg text-green-600">${customerDetails.stats.total_spent.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Tabs for Orders and Activity */}
              <Tabs defaultValue="orders">
                <TabsList>
                  <TabsTrigger value="orders" className="flex items-center gap-1">
                    <Package className="h-4 w-4" />
                    Orders ({customerDetails.orders.length})
                  </TabsTrigger>
                  <TabsTrigger value="activity" className="flex items-center gap-1">
                    <Activity className="h-4 w-4" />
                    Activity Log
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="orders" className="mt-4">
                  {customerDetails.orders.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No orders yet</p>
                  ) : (
                    <div className="space-y-3">
                      {customerDetails.orders.map((order) => (
                        <div key={order.id} className="border rounded p-3 text-sm">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                                {order.id.substring(0, 8)}...
                              </span>
                              <span className="ml-2 text-gray-500">
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              order.payment_status === 'paid' 
                                ? 'bg-green-100 text-green-800' 
                                : order.payment_status === 'pending_approval'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {order.payment_status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              {order.payment_method} - {order.maksura_tracking?.status?.replace('_', ' ') || 'N/A'}
                            </span>
                            <span className="font-bold">${order.total_amount.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="activity" className="mt-4">
                  {customerDetails.activity_log.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No activity logged</p>
                  ) : (
                    <div className="space-y-2">
                      {customerDetails.activity_log.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm border-b pb-2">
                          <Mail className="h-4 w-4 text-gray-400 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium">{log.subject}</p>
                            <p className="text-xs text-gray-500">
                              {log.template_type} - {log.status}
                            </p>
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(log.sent_at).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerManagement;
